import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import api from '../utils/api';

const OrderContext = createContext(null);

// How much a validated discount takes off a given subtotal. Recomputed on the
// client as the cart changes; the server always re-validates at checkout.
export const discountAmountFor = (discount, subtotal) => {
  if (!discount || !discount.valid) return 0;
  const sub = Number(subtotal) || 0;
  const raw = discount.type === 'percentage'
    ? (sub * Number(discount.value)) / 100
    : Number(discount.value);
  return Math.round(Math.max(0, Math.min(raw, sub)) * 100) / 100;
};

export const OrderProvider = ({ children }) => {
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null);

  const placeOrder = useCallback(async (orderData) => {
    setLoading(true);
    try {
      const data = await api('/orders', {
        method: 'POST',
        body: orderData,
      });
      const order = data.order || data;
      setCurrentOrder(order);
      return order;
    } catch (err) {
      console.error('Error placing order:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const applyDiscount = useCallback(async (code, subtotal) => {
    const trimmed = (code || '').trim();
    if (!trimmed) {
      setDiscount(null);
      return { valid: false, message: 'Enter a code' };
    }
    const res = await api('/cart/validate-discount', {
      method: 'POST',
      body: { code: trimmed, subtotal: Number(subtotal) || 0 },
    });
    if (res.valid) {
      setDiscount({
        valid: true,
        code: res.code || trimmed.toUpperCase(),
        type: res.type,
        value: Number(res.value),
        discountAmount: Number(res.discountAmount) || 0,
      });
    } else {
      setDiscount(null);
    }
    return res;
  }, []);

  const clearDiscount = useCallback(() => setDiscount(null), []);

  const getOrders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/orders');
      setOrders(data.orders || data || []);
      return data;
    } catch (err) {
      console.error('Error fetching orders:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getOrder = useCallback(async (id) => {
    setLoading(true);
    try {
      const data = await api(`/orders/${id}`);
      const order = data.order || data;
      setCurrentOrder(order);
      return order;
    } catch (err) {
      console.error('Error fetching order:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo(() => ({
    orders,
    currentOrder,
    loading,
    discount,
    placeOrder,
    getOrders,
    getOrder,
    applyDiscount,
    clearDiscount,
  }), [orders, currentOrder, loading, discount, placeOrder, getOrders, getOrder, applyDiscount, clearDiscount]);

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export default OrderContext;
